import { useContext, useState } from "react"
import _ from "lodash"
import PropTypes from "prop-types"
import { Steps } from "primereact/steps"
import { Card } from "primereact/card"
import gaTracking from "../../helpers/gaTracking"
import { confirmPopup } from "primereact/confirmpopup"

// Custom components
import { LoaderProgressBar, LoaderSmall } from "../../components/Loaders"
import FormErrorMessage from "./FormErrorMessage"
import { UserContext } from "../../context/userContext"
import CustomButton from "../../components/Buttons/CustomButton"

// Styles
import styles from "../../styles/Form.module.css"

export default function FormSubmitting(props) {
  const userCtx = useContext(UserContext)
  const [copied, setCopied] = useState(false)

  const handleCopyErrorInfo = () => {
    const text = [`Error code: ${transactionId}`, `Error message: ${message}`].join("\n")
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const {
    activeStep,
    error,
    message,
    transactionId,
    idStamp = "",
    templateData,
    formId,
    onGoBack,
    resubmitCount,
    resubmit,
  } = props

  const steps = [
    { label: "Validation" },
    { label: "Form Submission" },
    { label: "Confirmation" },
  ]

  const supportEmailSubject = `Submission error ${transactionId}`
  const supportEmailBody = [
    `Error code: ${transactionId}`,
    `Error message: ${message}`,
  ].join("\n")
  const supportEmailHref = `mailto:support@myrecoverylink.com?subject=${encodeURIComponent(
    supportEmailSubject,
  )}&body=${encodeURIComponent(supportEmailBody)}`

  const handleSteps = () => {
    switch (activeStep) {
      case 0:
        return (
          <>
            <h2>Validation Step</h2>
            <div>Please don't refresh your browser.</div>
          </>
        )
      case 1:
        return (
          <>
            <h2>Form Submission Step</h2>
            <div>
              Transaction ID:{" "}
              <code className={styles.errorCode}>{transactionId}</code>
            </div>
          </>
        )
      case 2: {
        /*Google Analytics tracking*/
        const gaTrackingParams = {
          event: "template_finish",
          userCtx,
          payload: {
            template_name: templateData.template.template_name,
            template_category: templateData.template.template_category,
            stamp_id: idStamp,
            transaction_id: transactionId,
            form_id: formId,
          },
        }
        gaTracking(gaTrackingParams)
        /*End Google Analytics tracking*/

        return (
          <>
            <h2 className={styles.successHeader}>
              <span className="material-icons">check_circle</span>Completed
              successfully.
            </h2>
            {message && <h3>{message}</h3>}
            <div className={styles.successMsg}>
              You will be redirected automatically.
            </div>

            <LoaderSmall />
          </>
        )
      }
      default:
        return null
    }
  }

  const handleError = () => {
    if (!error) return null

    /*Google Analytics tracking*/
    const gaTrackingParams = {
      event: "exception",
      userCtx,
      payload: {
        template_name: templateData.template.template_name,
        template_category: templateData.template.template_category,
        transaction_id: transactionId,
        stamp_id: idStamp,
        form_id: formId,
        error: message,
      },
    }
    gaTracking(gaTrackingParams)
    /*End Google Analytics tracking*/

    return (
      <div className={styles.errorContainer}>
        <h2 className={styles.errorContainerHeader}>
          <span className="material-icons">error</span>Error processing data.
        </h2>

        <h3>
          <span className={styles.errorContainerSubHeader}>
            Form submission was not made,
          </span>
          {" but customer support can help!"}
        </h3>

        {transactionId && (
          <h3 className={styles.errorSupportMsg}>
            Please{" "}
            <a className={styles.errorSupportMsgLink} href={supportEmailHref}>
              contact customer support
            </a>{" "}
            and provide the following:
          </h3>
        )}

        <div className={styles.errorCardWrapper}>
          <button
            className={styles.copyButton}
            onClick={handleCopyErrorInfo}
            title="Copy error info"
            type="button"
          >
            <span className="material-icons">
              {copied ? "check" : "content_copy"}
            </span>
          </button>
          <Card>
            <div>
              Error code:{" "}
              <code className={styles.errorCode}>{transactionId}</code>
            </div>
            <div>
              Error message:
              {!_.isEmpty(message) && <FormErrorMessage msg={message} />}
            </div>
          </Card>
        </div>

        <div className={styles.errorButtonContainer}>
          {/* COMING SOON: Option to resubmit the form after an error occurs. */}
          {/* <CustomButton
            label="Resubmit"
            severity="primary"
            onClick={resubmit}
            disabled={resubmitCount >= 1}
          /> */}
          <CustomButton
            label="Go back"
            severity="primary"
            outlined
            onClick={cancelConfirmation}
          />
        </div>
      </div>
    )
  }

  const cancelConfirmation = (event) => {
    confirmPopup({
      target: event.currentTarget,
      message: (
        <>
          All entered data will be lost.
          <br />
          Are you sure you want to proceed?
        </>
      ),
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "accept",
      accept() {
        onGoBack()
      },
    })
  }

  const displayLoader = !error && activeStep < 2

  return (
    <div>
      <div className={styles.stepsWrapper}>
        {displayLoader && <LoaderProgressBar />}
        <Steps
          model={steps}
          activeIndex={activeStep}
          readOnly={true}
          className={styles.stepsComponent}
        />
      </div>

      <div className={styles.stepsContent}>{handleSteps()}</div>
      {handleError()}
    </div>
  )
}

FormSubmitting.propTypes = {
  activeStep: PropTypes.number.isRequired,
  error: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
  idStamp: PropTypes.string.isRequired,
  formId: PropTypes.string.isRequired,
  templateData: PropTypes.object.isRequired,
  transactionId: PropTypes.string.isRequired,
  onGoBack: PropTypes.func.isRequired,
  resubmitCount: PropTypes.number.isRequired,
  resubmit: PropTypes.func.isRequired,
}
